// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Resilience: if env vars are missing in the deploy, surface a clear error in
// the browser console + a visible banner instead of crashing to a blank page.
// createClient(undefined, undefined, ...) throws synchronously, which would
// take down the entire React tree at module-import time -> blank dark screen
// (no way for the user to know what's wrong without DevTools).
const envMissing = !SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY;
if (envMissing && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    '[SnapChart] Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY ' +
    'environment variables. The Supabase client cannot initialize. ' +
    'Add them in Vercel: Project -> Settings -> Environment Variables, then redeploy.'
  );
  if (document.body) {
    document.body.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0a0e1a;color:#e5e7eb;font-family:system-ui,sans-serif;padding:24px;text-align:center;">
        <div style="max-width:560px;">
          <div style="font:600 10px/1.4 'SF Mono',Consolas,monospace;letter-spacing:3px;color:#ef4444;text-transform:uppercase;margin-bottom:16px;">▸ DEPLOY CONFIG ERROR</div>
          <h1 style="font-size:28px;font-weight:900;color:#fff;margin:0 0 12px;">Missing environment variables</h1>
          <p style="color:#9ca3af;line-height:1.6;margin:0 0 20px;">This deployment needs <code style="background:rgba(255,255,255,0.05);padding:2px 8px;border-radius:4px;color:#22d3ee;">VITE_SUPABASE_URL</code> and <code style="background:rgba(255,255,255,0.05);padding:2px 8px;border-radius:4px;color:#22d3ee;">VITE_SUPABASE_PUBLISHABLE_KEY</code> in Vercel.</p>
          <div style="background:rgba(34,211,238,0.05);border:1px solid rgba(34,211,238,0.2);border-radius:12px;padding:16px;text-align:left;font:13px/1.6 'SF Mono',Consolas,monospace;color:#e5e7eb;">
            <strong style="color:#22d3ee;">Fix:</strong><br>
            1. Vercel dashboard &rarr; this project &rarr; Settings &rarr; Environment Variables<br>
            2. Add both vars (copy from Supabase: Project Settings &rarr; API)<br>
            3. Deployments &rarr; latest &rarr; ... &rarr; Redeploy
          </div>
        </div>
      </div>`;
  }
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  SUPABASE_URL || 'https://placeholder.supabase.co',
  SUPABASE_PUBLISHABLE_KEY || 'placeholder-key',
  {
    auth: {
      storage: typeof window !== 'undefined' ? localStorage : undefined,
      persistSession: true,
      autoRefreshToken: true,
      // PKCE flow is more robust than the default implicit flow on
      // slow / high-latency networks. Implicit flow puts the whole
      // session (access + refresh token) in the URL hash; if the tab
      // is reopened later the SDK sees a stale hash and enters a
      // refresh loop that hits 429 Too Many Requests (Zack, Algeria).
      // PKCE exchanges a short-lived one-time code — no stale hash.
      flowType: 'pkce',
      // Explicit for clarity: strip the code / hash from the URL as
      // soon as the session is materialised so a reload cannot re-
      // trigger the exchange.
      detectSessionInUrl: true,
    },
    global: {
      // Slow-network resilience: default fetch has no timeout, so a
      // hung refresh_token request can pile up others. Wrap fetch to
      // abort after 30 s and let the SDK retry cleanly.
      fetch: (input, init) => {
        if (typeof window === 'undefined') return fetch(input, init);
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 30_000);
        return fetch(input, { ...init, signal: controller.signal })
          .finally(() => window.clearTimeout(timeoutId));
      },
    },
  }
);

// Belt-and-suspenders: after the SDK has processed the OAuth callback,
// remove the ?code=... query params and any hash from the URL so a
// browser back/reload can't re-enter the exchange and burn refresh
// quota. Runs once on module init.
if (typeof window !== 'undefined' && window.location) {
  const url = new URL(window.location.href);
  const hadOAuthParams = url.searchParams.has('code')
    || url.searchParams.has('state')
    || url.hash.includes('access_token=')
    || url.hash.includes('refresh_token=');
  if (hadOAuthParams) {
    // Give the SDK a tick to detectSessionInUrl, then scrub.
    setTimeout(() => {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }, 500);
  }
}